// METHUSELAH Source Selector
//
// Gen4 (Oura API) and Gen3 (BLE bridge) are the same hardware family —
// same sensors, same underlying vectors. This file is what makes them
// interchangeable: each vector is resolved independently from whichever
// source has fresh data, so the engine and the UI never need to know
// which ring produced a value.
//
// Priority per vector: Gen4 (fresh) -> Gen3 (fresh) -> manual -> null.
// A vector is null only when no source has data. Gen3 is only offered
// for a vector once its decoder is validated — see readiness notes below.
//
// Decoder readiness (see SESSION_HANDOFF.md for the latest status):
// - RHR: Gen3 READY (0x6A confirmed, cross-validated +/-1.1 bpm vs Gen4)
// - SpO2: Gen3 READY (0x6F confirmed — Track B condition #3 CLOSED 2026-07-08,
//   three consecutive nights within +/-5% gate: 1.9%, 4.5%, 3.2%). Resolved
//   below as telemetry only — there is no THRESHOLDS/COMMANDS entry for it,
//   so it does not participate in evaluate()'s priority cascade. Adding a
//   fifth command vector is a v3 discussion per the priority-order comment
//   in engine/index.js; this just makes the value/source interchangeable.
// - HRV: Gen3 READY (RMSSD from 0x6E/0x80 IBI, sleep-window only, validated 2026-07-13)
// - Sleep duration: Gen3 READY via daemon (0x6A state accumulation overnight).
//   Morning pull cannot provide this — ring buffer only holds ~10min of 0x6A tail.
// - Glucose: no wearable source on either generation — manual entry only

const FRESHNESS_WINDOW_MS = 24 * 60 * 60 * 1000;

export const SOURCE_GEN4 = "gen4_api";
export const SOURCE_GEN3 = "gen3_ble";
export const SOURCE_MANUAL = "manual";

// Kill switch, 2026-08-09: owner's explicit, informed override of the
// multi-night validation gate for sleep_duration_stage_sum_hrs (see
// pipeline/tools/tst_from_stages.py) -- accepted risk is a partial/reset
// 0x4C bout firing a false INITIATE SLEEP PROTOCOL command and feeding
// calculateBRI() on one confirmed night. Set to false to revert the Sleep
// Duration tile to a dash with no other code change -- flip this constant,
// nothing else, on a bad morning.
export const STAGE_SUM_FALLBACK_ENABLED = true;

function isFresh(timestamp) {
  if (!timestamp) return false;
  const age = Date.now() - new Date(timestamp).getTime();
  return age >= 0 && age < FRESHNESS_WINDOW_MS;
}

// candidates: priority-ordered [{ value, source }, ...] -- first non-null
// value wins. Array shape (rather than fixed positional args) so a fourth
// source (e.g. a future instrument) can be added by extending the caller's
// array, not by changing this function's signature.
export function resolveVector(candidates) {
  for (const { value, source } of candidates) {
    if (value != null) return { value, source, ready: true };
  }
  return { value: null, source: null, ready: false };
}

/**
 * Resolve the best available value for each engine vector.
 *
 * @param {Object|null} gen4 - Oura API state, shape { hrv, rhr, totalSleepHrs, isLive, timestamp }
 * @param {Object|null} gen3 - Gen3 bridge JSON, shape { timestamp, vectors: { rhr_bpm, hrv_ms, sleep_duration_hrs, ... } }
 * @param {Object} manual - manually entered values, shape { glucose }
 * @returns {{ glucose: Vector, hrv: Vector, rhr: Vector, sleepDurationHrs: Vector, spo2: Vector }}
 */
export function resolveVectors(gen4, gen3, manual = {}) {
  const gen4Fresh = gen4 && gen4.isLive && isFresh(gen4.timestamp) ? gen4 : null;
  const gen3Fresh = gen3 && isFresh(gen3.timestamp) ? gen3 : null;

  return {
    // n/agg: sample count + aggregation kind from Task 1b's bridge provenance
    // fields (gen3_bridge.py) -- gen3-only, no Gen4 equivalent exists (Gen4
    // is permanently dead; Task 1b never touched it). Null when the bridge
    // predates Task 1b or has no data this pull -- same ?. cascade as value.
    rhr: {
      ...resolveVector([
        { value: gen4Fresh?.rhr ?? null, source: SOURCE_GEN4 },
        { value: gen3Fresh?.vectors?.rhr_bpm ?? null, source: SOURCE_GEN3 },
        { value: null, source: SOURCE_MANUAL },
      ]),
      n: gen3Fresh?.vectors?.rhr_n ?? null,
      agg: gen3Fresh?.vectors?.rhr_agg ?? null,
    },

    // Telemetry only — not a THRESHOLDS/COMMANDS vector, doesn't drive evaluate()
    spo2: resolveVector([
      { value: gen4Fresh?.spo2 ?? null, source: SOURCE_GEN4 },
      { value: gen3Fresh?.vectors?.spo2_avg_pct ?? null, source: SOURCE_GEN3 },
      { value: null, source: SOURCE_MANUAL },
    ]),

    // Gen3: RMSSD computed from 0x6E/0x80 IBI streams, sleep-window only
    hrv: {
      ...resolveVector([
        { value: gen4Fresh?.hrv ?? null, source: SOURCE_GEN4 },
        { value: gen3Fresh?.vectors?.hrv_ms ?? null, source: SOURCE_GEN3 },
        { value: null, source: SOURCE_MANUAL },
      ]),
      n: gen3Fresh?.vectors?.hrv_n ?? null,
      agg: gen3Fresh?.vectors?.hrv_agg ?? null,
    },

    // Gen4: total_sleep_duration from Oura API. Gen3: sleep_duration_hrs (0x4C,
    // authoritative) if present, else sleep_duration_estimate_hrs (bout-tail
    // estimate), else -- if STAGE_SUM_FALLBACK_ENABLED -- sleep_duration_
    // stage_sum_hrs (light+rem+deep sum, robust to stage-subdivision error but
    // NOT to a partial/reset bout; see tst_from_stages.py). estimateMethod
    // tells the tile which one it's showing (null = strict, never hidden).
    // measuredAt: all three tiers ultimately come from the same 0x4C decode
    // step, which only ever runs in an offline recompute, never live in the
    // daemon's own per-cycle push -- see gen3_bridge.py's sleep_data_ts.
    // Attaching it here (instead of the tile falling back to the bridge's
    // general timestamp) is what makes a days-old sleep reading correctly
    // show as stale even while HRV/RHR are refreshing live every cycle.
    sleepDurationHrs: (() => {
      const measuredAt = gen3Fresh?.sleep_data_ts ?? null;
      // No n -- the ring's own firmware computes this, there's nothing to
      // count (see Task 1b, gen3_bridge.py). agg is present regardless of
      // which tier wins, or whether any tier has a value at all -- same
      // reasoning as estimateMethod/measuredAt below.
      const agg = gen3Fresh?.vectors?.sleep_duration_agg ?? null;
      const strict = resolveVector([
        { value: gen4Fresh?.totalSleepHrs ?? null, source: SOURCE_GEN4 },
        { value: gen3Fresh?.vectors?.sleep_duration_hrs ?? null, source: SOURCE_GEN3 },
        { value: null, source: SOURCE_MANUAL },
      ]);
      if (strict.value != null) {
        return { ...strict, estimateMethod: null, measuredAt: strict.source === SOURCE_GEN3 ? measuredAt : null, agg };
      }

      const boutTailHrs = gen3Fresh?.vectors?.sleep_duration_estimate_hrs ?? null;
      if (boutTailHrs != null) {
        return { value: boutTailHrs, source: SOURCE_GEN3, ready: true, estimateMethod: "bout_tail", measuredAt, agg };
      }

      if (STAGE_SUM_FALLBACK_ENABLED) {
        const stageSumHrs = gen3Fresh?.vectors?.sleep_duration_stage_sum_hrs ?? null;
        if (stageSumHrs != null) {
          return { value: stageSumHrs, source: SOURCE_GEN3, ready: true, estimateMethod: "stage_sum", measuredAt, agg };
        }
      }

      return { ...strict, estimateMethod: null, measuredAt: null, agg };
    })(),

    // No wearable source on either generation
    glucose: resolveVector([
      { value: null, source: SOURCE_GEN4 },
      { value: null, source: SOURCE_GEN3 },
      { value: manual?.glucose ?? null, source: SOURCE_MANUAL },
    ]),
  };
}
